import { Achievement, Badge, UserProgress } from '../types';
import { achievements, badges } from '../data/achievements';
import { ref, get, set } from 'firebase/database';
import { db } from '../lib/firebase';

export interface UserAchievements {
  achievements: Achievement[];
  badges: Badge[];
  totalPoints: number;
  unlockedCount: number;
  result?: boolean; // Optional result field for success/failure indication
}

export class AchievementService {
  private static instance: AchievementService;
  private currentUser: any;

  constructor(user: any) {
    this.currentUser = user;
  }

  static getInstance(user: any): AchievementService {
    if (!AchievementService.instance || AchievementService.instance.currentUser?.uid !== user?.uid) {
      AchievementService.instance = new AchievementService(user);
    }
    return AchievementService.instance;
  }

  async checkAndUnlockAchievements(userProgress: UserProgress, savedWords: any[] = []): Promise<Achievement[]> {
    if (!this.currentUser?.uid) return [];

    console.log('checkAndUnlockAchievements: Starting with user progress:', userProgress);
    
    const unlockedAchievements: Achievement[] = [];
    const userAchievementsRef = ref(db, `users/${this.currentUser.uid}/achievements`);
    const snapshot = await get(userAchievementsRef);
    const existingAchievements = snapshot.exists() ? snapshot.val() : {};
    
    console.log('checkAndUnlockAchievements: Existing achievements:', existingAchievements);

    for (const achievement of achievements) {
      console.log(`checkAndUnlockAchievements: Checking achievement ${achievement.name} (${achievement.id})`);
      
      // Skip if already unlocked
      if (existingAchievements[achievement.id]) {
        console.log(`checkAndUnlockAchievements: ${achievement.name} already unlocked, skipping`);
        continue;
      }

      const isUnlocked = await this.checkAchievementCondition(achievement, userProgress, savedWords);
      
      if (isUnlocked) {
        console.log(`checkAndUnlockAchievements: Unlocking ${achievement.name}!`);
        // Unlock the achievement
        await this.unlockAchievement(achievement);
        unlockedAchievements.push({
          ...achievement,
          unlockedAt: new Date()
        });
      } else {
        console.log(`checkAndUnlockAchievements: ${achievement.name} not unlocked yet`);
      }
    }

    console.log('checkAndUnlockAchievements: Total unlocked this run:', unlockedAchievements.length);
    return unlockedAchievements;
  }

  async checkAndUnlockBadges(userProgress: UserProgress): Promise<Badge[]> {
    if (!this.currentUser?.uid) return [];

    const unlockedBadges: Badge[] = [];
    const userBadgesRef = ref(db, `users/${this.currentUser.uid}/badges`);
    const snapshot = await get(userBadgesRef);
    const existingBadges = snapshot.exists() ? snapshot.val() : {};

    for (const badge of badges) {
      // Skip if already unlocked
      if (existingBadges[badge.id]) continue;

      const isUnlocked = await this.checkBadgeCondition(badge, userProgress);
      
      if (isUnlocked) {
        // Unlock the badge
        await this.unlockBadge(badge);
        unlockedBadges.push({
          ...badge,
          unlockedAt: new Date()
        });
      }
    }

    return unlockedBadges;
  }

  private async checkAchievementCondition(achievement: Achievement, userProgress: UserProgress, savedWords: any[]): Promise<boolean> {
    switch (achievement.condition.type) {
      case 'wordsLearned':
        const wordsLearnedCondition = userProgress.wordsLearned >= achievement.condition.value;
        console.log(`Checking ${achievement.name}: ${userProgress.wordsLearned} >= ${achievement.condition.value} = ${wordsLearnedCondition}`);
        return wordsLearnedCondition;

      case 'streak':
        const streakCondition = userProgress.streak >= achievement.condition.value;
        console.log(`Checking ${achievement.name}: ${userProgress.streak} >= ${achievement.condition.value} = ${streakCondition}`);
        return streakCondition;

      case 'themesCompleted':
        return await this.checkThemesCompleted(achievement.condition.value);

      case 'activitiesCompleted':
        return await this.checkActivitiesCompleted(achievement.condition.value);

      case 'perfectScore':
        return await this.checkPerfectScore();

      case 'practiceTime':
        return await this.checkPracticeTime(achievement.condition.value);

      case 'dailyGoal':
        return await this.checkDailyGoal();

      case 'crimeCasesSolved':
        return await this.checkCrimeCasesSolved(achievement.condition.value);

      case 'crimeHintsUsed':
        return await this.checkCrimeHintsUsed(achievement.condition.value);

      case 'crimeTime':
        return await this.checkCrimeTime(achievement.condition.value);

      case 'crimeStreak':
        return await this.checkCrimeStreak(achievement.condition.value);

      case 'dailyTrainingCompleted':
        return await this.checkDailyTrainingCompleted(achievement.condition.value);

      default:
        return false;
    }
  }

  private async checkBadgeCondition(badge: Badge, userProgress: UserProgress): Promise<boolean> {
    switch (badge.condition.type) {
      case 'dailyStreak':
        return await this.checkDailyStreak(badge.condition.value);

      case 'nightPractice':
        return await this.checkNightPractice();

      case 'weekendPractice':
        return await this.checkWeekendPractice(badge.condition.value);

      case 'perfectWeek':
        return await this.checkPerfectWeek(badge.condition.value);

      case 'socialSharing':
        return await this.checkSocialSharing(badge.condition.value);

      case 'monthlyGoal':
        return await this.checkMonthlyGoal();

      default:
        return false;
    }
  }

  private async checkThemesCompleted(requiredCount: number): Promise<boolean> {
    try {
      const themesRef = ref(db, `users/${this.currentUser.uid}/themes`);
      const snapshot = await get(themesRef);
      if (!snapshot.exists()) return false;

      const themes = snapshot.val();
      console.log('Checking themes completed. Available themes:', themes);
      
      const completedThemes = Object.values(themes).filter((theme: any) => 
        theme.progress >= 10 // Assuming 10 words per theme is completion
      );

      console.log(`Themes completed: ${completedThemes.length}/${requiredCount}`);
      return completedThemes.length >= requiredCount;
    } catch (error) {
      console.error('Error checking themes completed:', error);
      return false;
    }
  }

  // --- Crime Game Helpers ---

  private async checkCrimeCasesSolved(requiredCount: number): Promise<boolean> {
    try {
      // We need to query all attempts across all dates.
      // This might be expensive if structure is crimeGameAttempts/{date}/{uid}/{attemptId}
      // Better to check leaderboards or maintain a user stats aggregate for crime game.
      // Assuming we rely on raw attempts for now or global stats.
      // Let's check 'crimeGameLeaderboard/global/users/{uid}' for wins count.

      const statsRef = ref(db, `crimeGameLeaderboard/global/users/${this.currentUser.uid}`);
      const snapshot = await get(statsRef);

      if (!snapshot.exists()) return false;
      const stats = snapshot.val();

      return (stats.wins || 0) >= requiredCount;
    } catch (error) {
      console.error('Error checking crime cases solved:', error);
      return false;
    }
  }

  private async checkCrimeHintsUsed(maxHints: number): Promise<boolean> {
    // This is tricky because it's "Solve a case with X hints".
    // We need to find AT LEAST ONE winning attempt with <= maxHints.
    // Iterating all dates is hard.
    // Ideally, we should store "best performance" or iterate recent attempts.
    // For MVP, let's fetch a reasonable range of dates or use a new index?
    // Actually, `crimeGameAttempts` is organized by date.
    // We can try to fetch the last 30 days or something.
    // Or we can scan `crimeGameAttempts` for the user if possible.
    // Querying: `crimeGameAttempts` is `{date}/{uid}/{attemptId}`. Deeply nested for a global query.

    // Alternative: We can't easily query this without an index like `userGameAttempts/{uid}`.
    // However, we can try to check the *current* date or recent dates if feasible.
    // Let's try to fetch all dates from `crimeGameAttempts` (shallow) and then check user.
    // This is not scalable.

    // BETTER APPROACH for MVP:
    // Just check the leaderboard doesn't help with specific attempt details (hints).
    // Let's assume we can't easily backfill this check for old games without a better index.
    // But we can check *today's* game or recent games.

    // Let's try to get a list of dates first.
    try {
        // Limited check: Check last 7 days + today.
        const dates: string[] = [];
        const today = new Date();
        for(let i=0; i<30; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            // Format DD-MM-YYYY
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            dates.push(`${day}-${month}-${year}`);
        }

        for (const dateKey of dates) {
            const attemptRef = ref(db, `crimeGameAttempts/${dateKey}/${this.currentUser.uid}`);
            const snapshot = await get(attemptRef);
            if (snapshot.exists()) {
                const attempts = snapshot.val();
                // Check if any attempt is a win with <= maxHints
                const hasQualifyingWin = Object.values(attempts).some((a: any) =>
                    a.result === 'win' && a.hintsUsed <= maxHints
                );
                if (hasQualifyingWin) return true;
            }
        }
        return false;
    } catch (error) {
      console.error('Error checking crime hints used:', error);
      return false;
    }
  }

  private async checkCrimeTime(maxSeconds: number): Promise<boolean> {
     try {
        // Similar strategy to hints: Check recent history (last 30 days)
        const dates: string[] = [];
        const today = new Date();
        for(let i=0; i<30; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            dates.push(`${day}-${month}-${year}`);
        }

        for (const dateKey of dates) {
            const attemptRef = ref(db, `crimeGameAttempts/${dateKey}/${this.currentUser.uid}`);
            const snapshot = await get(attemptRef);
            if (snapshot.exists()) {
                const attempts = snapshot.val();
                const hasQualifyingWin = Object.values(attempts).some((a: any) =>
                    a.result === 'win' && a.timeSpentSec <= maxSeconds
                );
                if (hasQualifyingWin) return true;
            }
        }
        return false;
    } catch (error) {
      console.error('Error checking crime time:', error);
      return false;
    }
  }

  private async checkCrimeStreak(requiredStreak: number): Promise<boolean> {
    try {
        // Use Global Leaderboard 'streak' field if available?
        // GameLeaderboardEntry has 'streak'.
        const statsRef = ref(db, `crimeGameLeaderboard/global/users/${this.currentUser.uid}`);
        const snapshot = await get(statsRef);

        if (snapshot.exists()) {
            const stats = snapshot.val();
            return (stats.streak || 0) >= requiredStreak;
        }
        return false;
    } catch (error) {
      console.error('Error checking crime streak:', error);
      return false;
    }
  }

  // --- Daily Training Helpers ---

  private async checkDailyTrainingCompleted(requiredSessions: number): Promise<boolean> {
    try {
        // Check userStats for completedSessions count
        const statsRef = ref(db, `userStats/${this.currentUser.uid}`);
        const snapshot = await get(statsRef);

        if (snapshot.exists()) {
            const stats = snapshot.val();
            // Fallback to 0 if undefined
            return (stats.completedSessions || 0) >= requiredSessions;
        }
        return false;
    } catch (error) {
      console.error('Error checking daily training completed:', error);
      return false;
    }
  }

  private async checkActivitiesCompleted(requiredCount: number): Promise<boolean> {
    try {
      const activitiesRef = ref(db, `users/${this.currentUser.uid}/activitiesCompleted`);
      const snapshot = await get(activitiesRef);
      const count = snapshot.exists() ? snapshot.val() : 0;
      console.log(`Activities completed: ${count}/${requiredCount}`);
      return count >= requiredCount;
    } catch (error) {
      console.error('Error checking activities completed:', error);
      return false;
    }
  }

  private async checkPerfectScore(): Promise<boolean> {
    try {
      const perfectScoresRef = ref(db, `users/${this.currentUser.uid}/perfectScores`);
      const snapshot = await get(perfectScoresRef);
      const hasPerfectScore = snapshot.exists() && snapshot.val() > 0;
      console.log(`Perfect score check: ${hasPerfectScore}`);
      return hasPerfectScore;
    } catch (error) {
      console.error('Error checking perfect score:', error);
      return false;
    }
  }

  private async checkPracticeTime(requiredMinutes: number): Promise<boolean> {
    try {
      const practiceTimeRef = ref(db, `users/${this.currentUser.uid}/totalPracticeTime`);
      const snapshot = await get(practiceTimeRef);
      const totalMinutes = snapshot.exists() ? snapshot.val() : 0;
      return totalMinutes >= requiredMinutes;
    } catch (error) {
      console.error('Error checking practice time:', error);
      return false;
    }
  }

  private async checkDailyGoal(): Promise<boolean> {
    try {
      const dailyGoalRef = ref(db, `users/${this.currentUser.uid}/dailyGoal`);
      const snapshot = await get(dailyGoalRef);
      
      const defaultGoal = { target: 5 }; // default to 5 words a day
      const goal = snapshot.exists() ? snapshot.val() : defaultGoal;
      
      const today = new Date().toDateString();
      const todayProgressRef = ref(db, `users/${this.currentUser.uid}/dailyProgress/${today}`);
      const progressSnapshot = await get(todayProgressRef);
      
      if (!progressSnapshot.exists()) return false;
      
      const progress = progressSnapshot.val();
      return progress.wordsLearned >= goal.target;
    } catch (error) {
      console.error('Error checking daily goal:', error);
      return false;
    }
  }

  private async checkDailyStreak(requiredDays: number): Promise<boolean> {
    try {
      const practiceHistoryRef = ref(db, `users/${this.currentUser.uid}/practiceHistory`);
      const snapshot = await get(practiceHistoryRef);
      if (!snapshot.exists()) return false;

      const history = snapshot.val();
      
      const earlyBirdDates = Object.entries(history)
        .filter(([_, data]: [string, any]) => new Date(data.timestamp).getHours() < 9)
        .map(([date]) => new Date(date))
        .sort((a, b) => a.getTime() - b.getTime());

      if (earlyBirdDates.length < requiredDays) return false;

      let maxStreak = 1;
      let currentStreak = 1;

      for (let i = 1; i < earlyBirdDates.length; i++) {
        const diffTime = Math.abs(earlyBirdDates[i].getTime() - earlyBirdDates[i - 1].getTime());
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays === 1) {
          currentStreak++;
          maxStreak = Math.max(maxStreak, currentStreak);
        } else if (diffDays > 1) {
          currentStreak = 1;
        }
      }

      return maxStreak >= requiredDays;
    } catch (error) {
      console.error('Error checking daily streak:', error);
      return false;
    }
  }

  private async checkNightPractice(): Promise<boolean> {
    try {
      const lastPracticeRef = ref(db, `users/${this.currentUser.uid}/lastPractice`);
      const snapshot = await get(lastPracticeRef);
      if (!snapshot.exists()) return false;

      const lastPractice = new Date(snapshot.val());
      return lastPractice.getHours() >= 22; // After 10 PM
    } catch (error) {
      console.error('Error checking night practice:', error);
      return false;
    }
  }

  private async checkWeekendPractice(requiredWeekends: number): Promise<boolean> {
    try {
      const practiceHistoryRef = ref(db, `users/${this.currentUser.uid}/practiceHistory`);
      const snapshot = await get(practiceHistoryRef);
      if (!snapshot.exists()) return false;

      const history = snapshot.val();
      const weekendDates = Object.keys(history)
        .map(date => new Date(date))
        .filter(d => d.getDay() === 0 || d.getDay() === 6)
        .sort((a, b) => a.getTime() - b.getTime());

      if (weekendDates.length < requiredWeekends) return false;

      const getSaturday = (d: Date) => {
        const sat = new Date(d);
        sat.setDate(sat.getDate() - sat.getDay() + 6);
        return sat.toDateString();
      };

      const uniqueWeekends = [...new Set(weekendDates.map(getSaturday))]
        .map(dStr => new Date(dStr))
        .sort((a, b) => a.getTime() - b.getTime());

      if (uniqueWeekends.length < requiredWeekends) return false;

      let maxStreak = 1;
      let currentStreak = 1;

      for (let i = 1; i < uniqueWeekends.length; i++) {
        const diffTime = Math.abs(uniqueWeekends[i].getTime() - uniqueWeekends[i - 1].getTime());
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays === 7) {
          currentStreak++;
          maxStreak = Math.max(maxStreak, currentStreak);
        } else if (diffDays > 7) {
          currentStreak = 1;
        }
      }

      return maxStreak >= requiredWeekends;
    } catch (error) {
      console.error('Error checking weekend practice:', error);
      return false;
    }
  }

  private async checkPerfectWeek(requiredDays: number): Promise<boolean> {
    try {
      const practiceHistoryRef = ref(db, `users/${this.currentUser.uid}/practiceHistory`);
      const snapshot = await get(practiceHistoryRef);
      if (!snapshot.exists()) return false;

      const history = snapshot.val();
      const dates = Object.keys(history)
        .map(date => new Date(date))
        .sort((a, b) => a.getTime() - b.getTime());

      if (dates.length < requiredDays) return false;

      let maxStreak = 1;
      let currentStreak = 1;

      for (let i = 1; i < dates.length; i++) {
        const diffTime = Math.abs(dates[i].getTime() - dates[i - 1].getTime());
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays === 1) {
          currentStreak++;
          maxStreak = Math.max(maxStreak, currentStreak);
        } else if (diffDays > 1) {
          currentStreak = 1;
        }
      }

      return maxStreak >= requiredDays;
    } catch (error) {
      console.error('Error checking perfect week:', error);
      return false;
    }
  }

  private async checkSocialSharing(requiredShares: number): Promise<boolean> {
    try {
      const socialSharesRef = ref(db, `users/${this.currentUser.uid}/socialShares`);
      const snapshot = await get(socialSharesRef);
      const shares = snapshot.exists() ? snapshot.val() : 0;
      console.log(`Social shares: ${shares}/${requiredShares}`);
      return shares >= requiredShares;
    } catch (error) {
      console.error('Error checking social sharing:', error);
      return false;
    }
  }

  private async checkMonthlyGoal(): Promise<boolean> {
    try {
      const monthlyGoalRef = ref(db, `users/${this.currentUser.uid}/monthlyGoal`);
      const snapshot = await get(monthlyGoalRef);
      
      const defaultGoal = { target: 100 }; // default to 100 words a month
      const goal = snapshot.exists() ? snapshot.val() : defaultGoal;
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const monthProgressRef = ref(db, `users/${this.currentUser.uid}/monthlyProgress/${currentYear}/${currentMonth}`);
      const progressSnapshot = await get(monthProgressRef);
      
      if (!progressSnapshot.exists()) return false;
      
      const progress = progressSnapshot.val();
      const goalMet = progress.wordsLearned > goal.target;
      console.log(`Monthly goal check: ${progress.wordsLearned} > ${goal.target} = ${goalMet}`);
      return goalMet;
    } catch (error) {
      console.error('Error checking monthly goal:', error);
      return false;
    }
  }

  private async unlockAchievement(achievement: Achievement): Promise<void> {
    try {
      const achievementRef = ref(db, `users/${this.currentUser.uid}/achievements/${achievement.id}`);
      await set(achievementRef, {
        unlockedAt: Date.now(),
        points: achievement.points
      });

      // Update user's total points
      const userRef = ref(db, `users/${this.currentUser.uid}`);
      const userSnapshot = await get(userRef);
      const userData = userSnapshot.exists() ? userSnapshot.val() : {};
      
      await set(userRef, {
        ...userData,
        totalPoints: (userData.totalPoints || 0) + achievement.points
      });
    } catch (error) {
      console.error('Error unlocking achievement:', error);
    }
  }

  private async unlockBadge(badge: Badge): Promise<void> {
    try {
      const badgeRef = ref(db, `users/${this.currentUser.uid}/badges/${badge.id}`);
      await set(badgeRef, {
        unlockedAt: Date.now(),
        points: badge.points
      });

      // Update user's total points
      const userRef = ref(db, `users/${this.currentUser.uid}`);
      const userSnapshot = await get(userRef);
      const userData = userSnapshot.exists() ? userSnapshot.val() : {};
      
      await set(userRef, {
        ...userData,
        totalPoints: (userData.totalPoints || 0) + badge.points
      });
    } catch (error) {
      console.error('Error unlocking badge:', error);
    }
  }

  async getUserAchievements(): Promise<UserAchievements> {
    if (!this.currentUser?.uid) {
      return { achievements: [], badges: [], totalPoints: 0, unlockedCount: 0 };
    }

    try {
      console.log('getUserAchievements: Getting achievements for user:', this.currentUser.uid);
      
      const achievementsRef = ref(db, `users/${this.currentUser.uid}/achievements`);
      const badgesRef = ref(db, `users/${this.currentUser.uid}/badges`);
      
      const [achievementsSnapshot, badgesSnapshot] = await Promise.all([
        get(achievementsRef),
        get(badgesRef)
      ]);

      console.log('getUserAchievements: Achievements snapshot exists:', achievementsSnapshot.exists());
      console.log('getUserAchievements: Badges snapshot exists:', badgesSnapshot.exists());

      const unlockedAchievements = achievementsSnapshot.exists() 
        ? Object.entries(achievementsSnapshot.val()).map(([id, data]: [string, any]) => {
            const achievement = achievements.find(a => a.id === id);
            console.log(`getUserAchievements: Processing achievement ${id}:`, achievement);
            return achievement ? {
              ...achievement,
              unlockedAt: new Date(data.unlockedAt)
            } : null;
          }).filter(Boolean)
        : [];

      const unlockedBadges = badgesSnapshot.exists()
        ? Object.entries(badgesSnapshot.val()).map(([id, data]: [string, any]) => {
            const badge = badges.find(b => b.id === id);
            console.log(`getUserAchievements: Processing badge ${id}:`, badge);
            return badge ? {
              ...badge,
              unlockedAt: new Date(data.unlockedAt)
            } : null;
          }).filter(Boolean)
        : [];

      const totalPoints = unlockedAchievements.reduce((sum, a) => sum + (a?.points || 0), 0) +
                         unlockedBadges.reduce((sum, b) => sum + (b?.points || 0), 0);

      const result = {
        achievements: unlockedAchievements,
        badges: unlockedBadges,
        totalPoints,
        unlockedCount: unlockedAchievements.length + unlockedBadges.length
      };

      console.log('getUserAchievements: Final result:', result);
      return result;
    } catch (error) {
      console.error('Error getting user achievements:', error);
      return { achievements: [], badges: [], totalPoints: 0, unlockedCount: 0 };
    }
  }
} 